'use client'

/**
 * MadrasaProfileClient — the DISTINCT, education-centric profile for classification='madrasa' records.
 * Rendered instead of MasjidProfileClient (fork in render-profile.tsx). NO prayer-times / jumu'ah /
 * jamaat / qibla — a madrasa is a school. Every section renders ONLY from the madrasa's REAL registered
 * data (core columns + the `attributes` jsonb written at registration); a section with no data is
 * omitted (honest-empty) — never demo/placeholder content. Faculty + schedule have no data model yet,
 * so they are always honest-empty (not shown), never fabricated.
 */

import { useState } from 'react'
import Link from 'next/link'
import {
  GraduationCap, BookOpen, MapPin, Phone, CheckCircle2, Building2, Users,
  CalendarDays, Landmark, MessageCircle,
} from 'lucide-react'

const TEAL = '#0E7C61'
const GOLD = '#C79A3A'
const NAVY = '#0C2C3F'

type Attrs = {
  institutionType?: string; tagline?: string; classesRange?: string; medium?: string[]; board?: string
  gender?: string; dayResidential?: string; hostel?: boolean; studentCapacity?: number
  curriculum?: string[]; campusFacilities?: string[]; admissionsStatus?: string; academicYear?: string
}
type MadrasaLike = {
  id: string; slug?: string | null; nameEnglish: string; nameUrdu?: string | null
  city?: string | null; district?: string | null; state?: string | null; addressLine1?: string | null
  coverPhoto?: string | null; photos?: string[] | null; verified?: boolean | null
  establishedYear?: number | null; contactPhone?: string | null; whatsappNumber?: string | null
  description?: string | null; latitude?: string | number | null; longitude?: string | number | null
  attributes?: Attrs | null
}
type Announcement = { id: string; title: string; category: string; createdAt: string | null }

const ACADEMIC_RE = /academic|primary|secondary|nursery|class/i

export default function MadrasaProfileClient({ masjid, announcements = [] }: { masjid: MadrasaLike; announcements?: Announcement[] }) {
  const [tab, setTab] = useState<'overview' | 'programs' | 'facilities' | 'updates' | 'gallery'>('overview')
  const a: Attrs = masjid.attributes || {}

  const place = [masjid.city, masjid.state].filter(Boolean).join(', ')
  const photos = (masjid.photos || []).filter(Boolean)
  const hero = masjid.coverPhoto || photos[0] || null
  const curriculum = (a.curriculum || []).filter(Boolean)
  const islamic = curriculum.filter((c) => !ACADEMIC_RE.test(c))
  const academic = curriculum.filter((c) => ACADEMIC_RE.test(c))
  const facilities = (a.campusFacilities || []).filter(Boolean)
  const admissionsOpen = a.admissionsStatus === 'open'
  const hasCoords = masjid.latitude != null && masjid.longitude != null

  // stats — only the ones with real data
  const stats: { icon: React.ReactNode; value: string; label: string }[] = []
  if (a.studentCapacity) stats.push({ icon: <Users size={18} />, value: `${a.studentCapacity}+`, label: 'Students' })
  if (curriculum.length) stats.push({ icon: <BookOpen size={18} />, value: String(curriculum.length), label: 'Programs' })
  if (masjid.establishedYear) stats.push({ icon: <CalendarDays size={18} />, value: String(masjid.establishedYear), label: 'Established' })

  // Education snapshot rows — only populated ones
  const snapshot: { label: string; value: string }[] = []
  if (a.institutionType) snapshot.push({ label: 'Institution type', value: a.institutionType })
  if (a.board) snapshot.push({ label: 'Board / affiliation', value: a.board })
  if (a.classesRange) snapshot.push({ label: 'Classes', value: a.classesRange })
  if (a.gender) snapshot.push({ label: 'Students', value: a.gender })
  if (a.medium?.length) snapshot.push({ label: 'Medium', value: a.medium.join(' / ') })
  if (a.dayResidential) snapshot.push({ label: 'Type', value: a.dayResidential })
  if (typeof a.hostel === 'boolean') snapshot.push({ label: 'Hostel', value: a.hostel ? 'Available' : 'Not available' })

  const dirHref = hasCoords ? `https://www.google.com/maps/dir/?api=1&destination=${masjid.latitude},${masjid.longitude}` : null

  const card: React.CSSProperties = { background: '#fff', border: '1px solid #e7ecef', borderRadius: 16, padding: 18 }
  const h2: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, color: NAVY, fontSize: 17, fontWeight: 800, margin: '26px 0 12px' }
  const pill = (bg: string, fg: string): React.CSSProperties => ({ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 12px', borderRadius: 999, background: bg, color: fg, fontWeight: 700, fontSize: 13.5 })
  const tabBtn = (on: boolean): React.CSSProperties => ({ padding: '10px 4px', border: 'none', background: 'none', borderBottom: `2.5px solid ${on ? TEAL : 'transparent'}`, color: on ? TEAL : '#6a7883', fontWeight: on ? 800 : 600, fontSize: 14.5, cursor: 'pointer' })

  const emptyNote = (text: string) => <p style={{ color: '#8a97a0', fontSize: 14, fontStyle: 'italic', margin: '4px 0' }}>{text}</p>

  return (
    <main style={{ maxWidth: 720, margin: '0 auto', padding: '0 16px 80px', color: NAVY }}>
      {/* HERO */}
      <div style={{ position: 'relative', borderRadius: 18, overflow: 'hidden', marginTop: 16, background: `linear-gradient(135deg, ${NAVY}, ${TEAL})`, minHeight: hero ? undefined : 160 }}>
        {hero ? <img src={hero} alt={masjid.nameEnglish} style={{ width: '100%', height: 230, objectFit: 'cover', display: 'block' }} /> : (
          <div style={{ height: 160, display: 'grid', placeItems: 'center' }}><GraduationCap size={54} color="rgba(255,255,255,.85)" /></div>
        )}
      </div>

      {/* IDENTITY */}
      <div style={{ display: 'flex', gap: 14, marginTop: -34, padding: '0 8px', alignItems: 'flex-end' }}>
        <div style={{ width: 76, height: 76, borderRadius: 18, background: '#fff', border: `3px solid #fff`, boxShadow: '0 4px 14px rgba(0,0,0,.12)', display: 'grid', placeItems: 'center', flex: '0 0 auto' }}>
          <GraduationCap size={38} color={TEAL} />
        </div>
      </div>
      <h1 style={{ fontSize: 25, fontWeight: 800, margin: '12px 0 2px' }}>{masjid.nameEnglish}</h1>
      {a.institutionType ? <div style={{ color: TEAL, fontWeight: 700, fontSize: 15 }}>{a.institutionType}</div> : null}
      {masjid.verified ? <div style={{ marginTop: 8 }}><span style={pill('rgba(14,124,97,.1)', TEAL)}><CheckCircle2 size={15} /> Verified institution</span></div> : null}
      {a.tagline ? <p style={{ color: GOLD, fontWeight: 600, fontStyle: 'italic', marginTop: 10, fontSize: 15.5 }}>{a.tagline}</p> : null}
      {place ? <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#5a6a74', marginTop: 8, fontSize: 14.5 }}><MapPin size={15} /> {[masjid.addressLine1, place].filter(Boolean).join(', ')}</div> : null}

      {/* ACTIONS */}
      <div style={{ display: 'flex', gap: 10, marginTop: 16, flexWrap: 'wrap' }}>
        {dirHref ? <a href={dirHref} target="_blank" rel="noopener noreferrer" style={{ ...pill(TEAL, '#fff'), textDecoration: 'none' }}><MapPin size={15} /> Get Directions</a> : null}
        {masjid.contactPhone ? <a href={`tel:${masjid.contactPhone}`} style={{ ...pill('#fff', NAVY), border: '1px solid #d9e0e4', textDecoration: 'none' }}><Phone size={15} /> Call</a> : null}
        {masjid.whatsappNumber ? <a href={`https://wa.me/${String(masjid.whatsappNumber).replace(/\D/g, '')}`} target="_blank" rel="noopener noreferrer" style={{ ...pill('#fff', '#128C7E'), border: '1px solid #d9e0e4', textDecoration: 'none' }}><MessageCircle size={15} /> WhatsApp</a> : null}
      </div>

      {/* STATS — only populated */}
      {stats.length ? (
        <div style={{ display: 'flex', gap: 10, marginTop: 18 }}>
          {stats.map((s) => (
            <div key={s.label} style={{ ...card, flex: 1, textAlign: 'center', padding: 14 }}>
              <div style={{ color: TEAL, display: 'flex', justifyContent: 'center' }}>{s.icon}</div>
              <div style={{ fontSize: 19, fontWeight: 800, marginTop: 4 }}>{s.value}</div>
              <div style={{ fontSize: 12, color: '#7a8892' }}>{s.label}</div>
            </div>
          ))}
        </div>
      ) : null}

      {/* ADMISSIONS banner — only when open */}
      {admissionsOpen ? (
        <div style={{ ...card, marginTop: 18, background: 'linear-gradient(100deg, rgba(199,154,58,.12), rgba(14,124,97,.08))', borderColor: 'rgba(199,154,58,.4)', display: 'flex', alignItems: 'center', gap: 12 }}>
          <Landmark size={22} color={GOLD} />
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 800 }}>Admissions Open{a.academicYear ? ` · ${a.academicYear}` : ''}</div>
            <div style={{ fontSize: 13, color: '#6a7883' }}>Enquire about enrolment for this academic year.</div>
          </div>
          {masjid.contactPhone ? <a href={`tel:${masjid.contactPhone}`} style={{ ...pill(GOLD, '#fff'), textDecoration: 'none' }}>Enquire</a> : null}
        </div>
      ) : null}

      {/* TABS */}
      <div style={{ display: 'flex', gap: 18, borderBottom: '1px solid #e7ecef', marginTop: 22 }}>
        {(['overview', 'programs', 'facilities', 'updates', 'gallery'] as const).map((k) => (
          <button key={k} type="button" onClick={() => setTab(k)} style={tabBtn(tab === k)}>{k[0].toUpperCase() + k.slice(1)}</button>
        ))}
      </div>

      {tab === 'overview' ? (
        <>
          {masjid.description ? (
            <>
              <div style={h2}><Building2 size={18} color={TEAL} /> About the institution</div>
              <p style={{ color: '#41525c', lineHeight: 1.6, fontSize: 15 }}>{masjid.description}</p>
            </>
          ) : null}
          <div style={h2}><BookOpen size={18} color={TEAL} /> Education snapshot</div>
          {snapshot.length ? (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              {snapshot.map((r) => (
                <div key={r.label} style={card}>
                  <div style={{ fontSize: 12, color: '#8a97a0', fontWeight: 700 }}>{r.label}</div>
                  <div style={{ fontWeight: 700, marginTop: 2 }}>{r.value}</div>
                </div>
              ))}
            </div>
          ) : emptyNote('No education details added yet.')}
        </>
      ) : null}

      {tab === 'programs' ? (
        <>
          <div style={h2}><BookOpen size={18} color={TEAL} /> Programs & curriculum</div>
          {curriculum.length ? (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              {academic.length ? (
                <div style={card}><div style={{ fontWeight: 800, marginBottom: 8, display: 'flex', gap: 6, alignItems: 'center' }}><GraduationCap size={16} color={TEAL} /> Academic</div>{academic.map((c) => <div key={c} style={{ fontSize: 14, color: '#41525c', padding: '3px 0' }}>• {c}</div>)}</div>
              ) : null}
              {islamic.length ? (
                <div style={card}><div style={{ fontWeight: 800, marginBottom: 8, display: 'flex', gap: 6, alignItems: 'center' }}><Landmark size={16} color={GOLD} /> Islamic studies</div>{islamic.map((c) => <div key={c} style={{ fontSize: 14, color: '#41525c', padding: '3px 0' }}>• {c}</div>)}</div>
              ) : null}
            </div>
          ) : emptyNote('No programs listed yet.')}
        </>
      ) : null}

      {tab === 'facilities' ? (
        <>
          <div style={h2}><Building2 size={18} color={TEAL} /> Campus facilities</div>
          {facilities.length ? (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {facilities.map((f) => <span key={f} style={pill('rgba(14,124,97,.08)', NAVY)}>{f}</span>)}
            </div>
          ) : emptyNote('No campus facilities listed yet.')}
        </>
      ) : null}

      {tab === 'updates' ? (
        <>
          <div style={h2}><CalendarDays size={18} color={TEAL} /> Latest updates</div>
          {announcements.length ? announcements.map((u) => (
            <div key={u.id} style={{ ...card, marginBottom: 10 }}>
              <div style={{ fontWeight: 700 }}>{u.title}</div>
              {u.createdAt ? <div style={{ fontSize: 12, color: '#8a97a0', marginTop: 2 }}>{new Date(u.createdAt).toLocaleDateString()}</div> : null}
            </div>
          )) : emptyNote('No updates posted yet.')}
        </>
      ) : null}

      {tab === 'gallery' ? (
        <>
          <div style={h2}><GraduationCap size={18} color={TEAL} /> Campus life</div>
          {photos.length ? (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
              {photos.map((p, i) => <img key={i} src={p} alt={`${masjid.nameEnglish} photo ${i + 1}`} style={{ width: '100%', height: 110, objectFit: 'cover', borderRadius: 10 }} />)}
            </div>
          ) : emptyNote('No photos added yet.')}
        </>
      ) : null}

      <div style={{ marginTop: 40, textAlign: 'center' }}>
        <Link href="/directory" style={{ color: TEAL, fontWeight: 700, textDecoration: 'none' }}>← Back to directory</Link>
      </div>
    </main>
  )
}
